import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import FormContainer from "../components/FormContainer";
import StepsHeader from "../components/StepsHeader";

import StepsNotification from "../components/StepsNotification";
import { getSeniorHomesRequest, saveShippingAddress } from "../redux/actions";
import { AppState } from "../redux/types";
import api from "../utils/api-helper/api";
import { localePath } from "../i18n/config";
import { CATEGORIES, categoryOptions, getPricingRegion, REGIONS, SHIPPING_METHODS } from "../utils/constants";
import { getCurrencySymbol } from "../utils/currency";

type ShippingMethod = (typeof SHIPPING_METHODS)[keyof typeof SHIPPING_METHODS];

interface BtnDisableParams {
    shippingMethod: ShippingMethod | undefined;
    name: string | undefined;
    address: string | undefined;
    postalCode: string | undefined;
    city: string | undefined;
    country: string | undefined;
    pickupPoint: any | undefined;
}

const ShippingScreen = () => {
    const cart = useSelector((state: AppState) => state.cart);
    const user = useSelector((state: AppState) => state.user);
    const shippingMethods = useSelector((state: AppState) => state.shippingMethods.shipping);
    const shipping_direct = shippingMethods?.find((el) => el.shippingMethod === SHIPPING_METHODS.direct);
    const shipping_posti = shippingMethods?.find((el) => el.shippingMethod === SHIPPING_METHODS.posti);
    const shipping_posti_gadgets = shippingMethods?.find((el) => el.shippingMethod === SHIPPING_METHODS.posti_gadgets);
    const shipping_office_pickup = shippingMethods?.find((el) => el.shippingMethod === SHIPPING_METHODS.office_pickup);
    const rawCategory = useSelector((state: AppState) => state.categories.category?.attributes.slug);
    const category = CATEGORIES[rawCategory as keyof typeof CATEGORIES] || rawCategory;
    const [shippingMethod, setShippingMethod] = useState(cart.shippingMethod);
    const [name, setName] = useState(cart.name || `${user.firstName} ${user.lastName}`);
    const [address, setAddress] = useState(cart.address);
    const [postalCode, setPostalCode] = useState(cart.postalCode);
    const [city, setCity] = useState(cart.city);
    const regionCode = useSelector((state: AppState) => state.region?.code ?? "FI");
    const checkoutRegion = sessionStorage.getItem("confirmedBuyRegion");
    const orderRegionCode = checkoutRegion || regionCode;
    const pricingRegion = getPricingRegion(orderRegionCode);
    const shippingCountryOptions = REGIONS.filter((r, index, all) => all.findIndex((item) => item.countryCode === r.countryCode) === index);
    const currencySymbol = getCurrencySymbol(orderRegionCode);
    const [country, setCountry] = useState(cart.country || pricingRegion);
    const [shippingCountryCode, setShippingCountryCode] = useState(cart.country || pricingRegion);
    const [email, setEmail] = useState(cart.email || user.email);
    const [mobile, setMobile] = useState(cart.mobile || user.mobileNumber);
    const [pickupPoints, setPickupPoints] = useState([]);
    const [pickupPoint, setPickupPoint] = useState(cart.pickupPoint);
    const [usePickupPoint, setUsePickupPoint] = useState(pricingRegion === "FI");
    const [pickupPointLoading, setPickupPointLoading] = useState(false);
    // const [freeTierCode, setFreeTierCode] = useState('')
    // const [isFree, setIsFree] = useState(false)

    const { list } = useSelector((state: AppState) => state.seniorHomes);
    const [nursingHome, setNursingHome] = useState(cart.nursingHome || list?.filter((el) => el.show)?.[0]?.reference);

    const history = useHistory();
    const dispatch = useDispatch();

    const isFieldMissing = (...fields: (string | undefined)[]): boolean => fields.some((field) => !field);
    const isPickupPointMissing = (pickupPoint: any): boolean => !pickupPoint || !pickupPoint.id;
    const shouldDisableButton = ({ shippingMethod, name, address, postalCode, city, country, pickupPoint }: BtnDisableParams): boolean => {
        const validMethods = Object.values(SHIPPING_METHODS);
        if (!shippingMethod || !validMethods.includes(shippingMethod)) {
            return true;
        }
        if (shippingMethod === SHIPPING_METHODS.posti || shippingMethod === SHIPPING_METHODS.posti_gadgets) {
            if (pricingRegion !== "FI") return isFieldMissing(name, address, postalCode, city, country);
            return isFieldMissing(name, address, postalCode, city, country) || isPickupPointMissing(pickupPoint);
        }
        if (shippingMethod === SHIPPING_METHODS.direct) {
            return isFieldMissing(nursingHome);
        }
        return false;
    };

    const [btnDisabled, setBtnDisabled] = useState<boolean>(
        shouldDisableButton({
            shippingMethod: shippingMethod as ShippingMethod,
            name,
            address,
            postalCode,
            city,
            country,
            pickupPoint,
        })
    );

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        setBtnDisabled(
            shouldDisableButton({
                shippingMethod: shippingMethod as ShippingMethod,
                name,
                address,
                postalCode,
                city,
                country,
                pickupPoint,
            })
        );
    }, [shippingMethod, name, address, postalCode, city, country, pickupPoint, nursingHome]);

    useEffect(() => {
        setCountry((prev) => prev || pricingRegion);
        setShippingCountryCode((prev) => prev || pricingRegion);
    }, [pricingRegion]);

    useEffect(() => {
        if (!nursingHome && list) {
            const firstVisible = list.filter((el) => el.show)[0];
            if (firstVisible) setNursingHome(firstVisible.reference);
        }
    }, [list]);

    const checkPostalCode = useCallback(() => {
        if (pricingRegion === "FI") {
            return postalCode?.length === 5;
        }
        return true;
    }, [postalCode, pricingRegion]);

    const fetchPickupPoints = useCallback(() => {
        setPickupPointLoading(true);
        api.get(`/user/orders/points?zipCode=${postalCode}`)
            .then((resp) => {
                if (resp.status === 200) {
                    setPickupPoints(resp.data.payload);
                } else {
                    setPickupPoints([]);
                }
            })
            .catch((err) => {
                setPickupPoints([]);
            })
            .finally(() => {
                setPickupPointLoading(false);
            });
    }, [postalCode]);

    useEffect(() => {
        if (!checkPostalCode()) return;
        fetchPickupPoints();
    }, [fetchPickupPoints, checkPostalCode]);

    useEffect(() => {
        if (postalCode === cart.postalCode) {
            setPickupPoint(cart.pickupPoint);
        } else {
            setPickupPoint({
                address1: null,
                address2: null,
                city: null,
                contact: null,
                countryCode: null,
                email: null,
                fax: null,
                id: null,
                name: null,
                openingHours: null,
                phone: null,
                serviceCode: null,
                serviceType: null,
                sms: null,
                state: null,
                zipcode: null,
            });
        }
    }, [setPostalCode, cart.pickupPoint, cart.postalCode, postalCode]);

    const submitHandler = (e: React.FormEvent) => {
        e.preventDefault();

        dispatch(
            saveShippingAddress({
                shippingMethod,
                name,
                address,
                postalCode,
                city,
                country: country || pricingRegion,
                email,
                mobile,
                pickupPoint,
                usePickupPoint,
                nursingHome,
            })
        );
        history.push(localePath("maksutapa"));
    };

    useEffect(() => {
        dispatch(getSeniorHomesRequest());
    }, [dispatch]);


    const isAnyPosti = shippingMethod === SHIPPING_METHODS.posti || shippingMethod === SHIPPING_METHODS.posti_gadgets;
    const isFinnish = pricingRegion === "FI";
    return (
        <>
            <StepsHeader step1 step2 step3 user={2} payment={1} account={1} />
            <FormContainer>
                <h1 className="shipping-header">Valitse toimitustapa</h1>
                <Form className="shipping-form" onSubmit={submitHandler}>
                    <Form.Group controlId="shippingMethod">
                        {/*<Form.Label>Anna koodi, jos sinulla on ilmainen kokeilu -koodi</Form.Label>*/}
                        <Form.Check
                            className="radio-button"
                            type="radio"
                            label={
                                <div>
                                    <Row>
                                        <Col xs={10}>Toimitus asiakkaalle Postin noutopisteeseen</Col>
                                        <Col xs={2}>{(category === categoryOptions.accessories ? shipping_posti_gadgets?.price || 0 : shipping_posti?.price || 0).toFixed(2)} {currencySymbol}</Col>
                                    </Row>
                                </div>
                            }
                            id={category === categoryOptions.accessories ? SHIPPING_METHODS.posti_gadgets : SHIPPING_METHODS.posti}
                            name="shippingMethod"
                            value={category === categoryOptions.accessories ? SHIPPING_METHODS.posti_gadgets : SHIPPING_METHODS.posti}
                            checked={shippingMethod === (category === categoryOptions.accessories ? SHIPPING_METHODS.posti_gadgets : SHIPPING_METHODS.posti)}
                            onChange={(e) => setShippingMethod(e.target.value)}
                        />
                        {isFinnish && category !== categoryOptions.alarm && category !== categoryOptions.accessories && (
                            <Form.Check
                                className="radio-button"
                                type="radio"
                                label={
                                    <div>
                                        <Row>
                                            <Col xs={10}>Yhteistilaus kotisairaanhoitoon, palveluasuntoon tai hoivakotiin</Col>
                                            <Col xs={2}>{(shipping_direct?.price || 0).toFixed(2)} {currencySymbol}</Col>
                                        </Row>
                                        <Row>
                                            <Col xs={10}>
                                                <small className="for-example">Valitse tämä vain, jos yhteistilauksesta on sovittu edellä mainitun tuottajan kanssa</small>
                                            </Col>
                                        </Row>
                                    </div>
                                }
                                id={SHIPPING_METHODS.direct}
                                name="shippingMethod"
                                value={SHIPPING_METHODS.direct}
                                checked={shippingMethod === SHIPPING_METHODS.direct}
                                onChange={(e) => setShippingMethod(e.target.value)}
                            />
                        )}
                        {isFinnish && (category === categoryOptions.phone || category === categoryOptions.tablet) && (
                            <Form.Check
                                className="radio-button"
                                type="radio"
                                label={
                                    <div>
                                        <Row>
                                            <Col xs={10}>Nouto Digihappyn toimistolta sovittuun aikaan</Col>
                                            <Col xs={2}>{(shipping_office_pickup?.price || 0).toFixed(2)} {currencySymbol}</Col>
                                        </Row>
                                    </div>
                                }
                                id={SHIPPING_METHODS.office_pickup}
                                name="shippingMethod"
                                value={SHIPPING_METHODS.office_pickup}
                                checked={shippingMethod === SHIPPING_METHODS.office_pickup}
                                onChange={(e) => setShippingMethod(e.target.value)}
                            />
                        )}
                    </Form.Group>
                    {category !== categoryOptions.alarm && category !== categoryOptions.accessories && (
                        <div
                            id="contact-info"
                            style={{
                                display: shippingMethod === SHIPPING_METHODS.direct ? "block" : "none",
                            }}
                        >
                            <Form.Group>
                                <Form.Label>Valitse kotisairaanhoito, palveluasunto tai hoivakoti</Form.Label>
                                <Form.Control as="select" aria-label="Asuu vanhainkodissa" value={nursingHome} onChange={(e) => setNursingHome(e.target.value)}>
                                    {list
                                        ?.filter((el) => el.show)
                                        ?.map((item) => {
                                            return (
                                                <option key={`nursing_home_${item.reference}`} id={item.reference} value={item.reference} disabled={!item.show}>
                                                    {item.name}
                                                </option>
                                            );
                                        })}
                                </Form.Control>
                            </Form.Group>
                        </div>
                    )}
                    <div
                        id="contact-info"
                        style={{
                            display: shippingMethod === SHIPPING_METHODS.posti || shippingMethod === SHIPPING_METHODS.posti_gadgets ? "block" : "none",
                        }}
                    >
                        <Form.Group controlId="name">
                            <Form.Label>*Nimi</Form.Label>
                            <Form.Control className="notranslate" translate="no" type="text" placeholder="Nimi" name="name" value={name} onChange={(e) => setName(e.target.value)} required={isAnyPosti} />
                            {/*<small className="for-example">John Doe</small>*/}
                        </Form.Group>
                        <Form.Group controlId="address">
                            <Form.Label>*Katusoite</Form.Label>
                            <Form.Control className="notranslate" translate="no" type="text" placeholder="Katuosoite" name="address" onChange={(e) => setAddress(e.target.value)} value={address} required={isAnyPosti} />
                            {/*<small className="for-example">Tekniikantie 2</small>*/}
                        </Form.Group>
                        <Form.Group controlId="postal-code">
                            <Form.Label>*Postinumero</Form.Label>
                            <Form.Control className="notranslate" translate="no" type="text" placeholder="Postinumero" name="postalCode" onChange={(e) => setPostalCode(e.target.value)} value={postalCode} required={isAnyPosti} />
                            {isFinnish && <small className="for-example">Postinumero on viidestä numerosta muodostuva koodi (esim. 00100). </small>}
                        </Form.Group>
                        <Form.Group controlId="city">
                            <Form.Label>*Kaupunki</Form.Label>
                            <Form.Control className="notranslate" translate="no" type="text" placeholder="Kaupunki" name="city" onChange={(e) => setCity(e.target.value)} value={city} required={isAnyPosti} />
                            {/*<small className="for-example">Espoo</small>*/}
                        </Form.Group>
                        <Form.Group controlId="country">
                            <Form.Label>*Maa</Form.Label>
                            <Form.Control
                                as="select"
                                className="notranslate"
                                translate="no"
                                name="country"
                                value={shippingCountryCode}
                                required={isAnyPosti}
                                disabled
                            >
                                {shippingCountryOptions.map((r) => (
                                    <option key={r.countryCode} value={r.countryCode}>
                                        {r.flag} {r.countryCode}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group controlId="email">
                            <Form.Label>*Sähköpostiosoite</Form.Label>
                            <Form.Control className="notranslate" translate="no" type="email" placeholder="Sähköpostiosoite" name="email" onChange={(e) => setEmail(e.target.value)} value={email} required={isAnyPosti} />
                            {/*<small className="for-example">digihappy@mediti.fi</small>*/}
                        </Form.Group>
                        <Form.Group controlId="mobile">
                            <Form.Label>*Puhelin</Form.Label>
                            <Form.Control className="notranslate" translate="no" type="text" placeholder="Puhelin" name="mobile" onChange={(e) => setMobile(e.target.value)} value={mobile} required={isAnyPosti} />
                            <small className="for-example">Esimerkiksi +358 40 123456</small>
                        </Form.Group>
                        {isFinnish && (
                        <Form.Group controlId="mobile">
                            <Form.Check
                                type="checkbox"
                                label={`Valitse postitoimituksen noutopaikka`}
                                id="usePickupPoint"
                                name="usePickupPoint"
                                checked={usePickupPoint}
                                disabled={true}
                                onChange={(e) => setUsePickupPoint(e.target.checked)}
                            />
                        </Form.Group>
                        )}
                        {isFinnish && usePickupPoint &&
                            checkPostalCode() &&
                            (pickupPointLoading ? (
                                "Noutopisteet latautuvat.."
                            ) : (
                                <Form.Group>
                                    {pickupPoints.length > 0
                                        ? pickupPoints.map((el: any, index) => (
                                              <Form.Check
                                                  key={index}
                                                  className="radio-button"
                                                  type="radio"
                                                  label={`${el?.name} (${el?.address1}, ${el?.city})`}
                                                  id={`pickupPoint-${el?.id}`}
                                                  name="pickupPoint"
                                                  value={el?.id}
                                                  checked={el?.id === pickupPoint?.id}
                                                  onChange={(e) => setPickupPoint(el)}
                                              />
                                          ))
                                        : pickupPoints?.length === 0 && (
                                              <div>
                                                  <div>Ei löydetty</div>
                                                  <Button variant="outline-primary" onClick={fetchPickupPoints}>
                                                      Lataa luettelo uudelleen
                                                  </Button>
                                              </div>
                                          )}
                                </Form.Group>
                            ))}
                    </div>
                    <Button type="submit" className="tallenna my-4" disabled={btnDisabled}>
                        Seuraava
                    </Button>
                </Form>
            </FormContainer>
            <StepsNotification />
        </>
    );
};

export default ShippingScreen;
